import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DoctorCard from "@/components/DoctorCard";
import { EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const FEES = { any: [null, null], u400: [null, 400], "400-700": [400, 700], o700: [700, null] };

export function SearchFilters({ value, onChange, onSubmit, compact }) {
  const [specs, setSpecs] = useState([]);
  useEffect(() => { api.get("/specialties").then((r) => setSpecs(r.data)).catch(() => {}); }, []);
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }} className={`grid gap-3 ${compact ? "sm:grid-cols-2 lg:grid-cols-5" : "sm:grid-cols-2 lg:grid-cols-6"}`} data-testid="doctor-search-form">
      {!compact && (
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 muted" />
          <Input value={value.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search doctor by name" className="pl-9 h-11 rounded-xl" data-testid="doctor-search-input" />
        </div>
      )}
      <Select value={value.specialty} onValueChange={set("specialty")}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="filter-specialty"><SelectValue placeholder="Specialty" /></SelectTrigger>
        <SelectContent><SelectItem value="any">All specialties</SelectItem>{specs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={value.available} onValueChange={set("available")}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="filter-availability"><SelectValue placeholder="Availability" /></SelectTrigger>
        <SelectContent><SelectItem value="any">Any availability</SelectItem><SelectItem value="today">Available today</SelectItem><SelectItem value="week">Next 7 days</SelectItem></SelectContent>
      </Select>
      <Select value={value.mode} onValueChange={set("mode")}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
        <SelectContent><SelectItem value="any">Video or audio</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="audio">Audio</SelectItem></SelectContent>
      </Select>
      <Select value={value.fee} onValueChange={set("fee")}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="filter-fee"><SelectValue placeholder="Fee" /></SelectTrigger>
        <SelectContent><SelectItem value="any">Any fee</SelectItem><SelectItem value="u400">Up to ₹400</SelectItem><SelectItem value="400-700">₹400 – ₹700</SelectItem><SelectItem value="o700">Above ₹700</SelectItem></SelectContent>
      </Select>
      {compact && <Button type="submit" className="h-11 rounded-xl" data-testid="doctor-search-submit"><Search className="h-4 w-4 mr-2" />Find doctors</Button>}
    </form>
  );
}

export const emptyFilters = { q: "", specialty: "any", available: "any", mode: "any", fee: "any" };

export const filtersToParams = (f) => {
  const [min, max] = FEES[f.fee];
  return { q: f.q || undefined, specialty: f.specialty !== "any" ? f.specialty : undefined, available: f.available !== "any" ? f.available : undefined,
    mode: f.mode !== "any" ? f.mode : undefined, min_fee: min ?? undefined, max_fee: max ?? undefined };
};

export default function DoctorDirectory({ initial = emptyFilters, bookPath, scope }) {
  const [f, setF] = useState(initial);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      setList(null);
      api.get("/doctors", { params: { ...filtersToParams(f), ...(scope || {}) } }).then((r) => { setList(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);
  return (
    <div>
      <div className="surface p-4"><SearchFilters value={f} onChange={setF} /></div>
      <div className="mt-6">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!list && !error && <Loader2 className="h-6 w-6 animate-spin text-[#086B2D]" />}
        {list && list.length === 0 && <EmptyState title="No doctors match these filters" text="Try a different specialty, mode or fee range." />}
        {list && list.length > 0 && (
          <>
            <p className="text-sm muted mb-4" data-testid="doctor-results-count">{list.length} verified doctor{list.length > 1 ? "s" : ""} available</p>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{list.map((d) => <DoctorCard key={d.id} d={d} bookPath={bookPath} />)}</div>
          </>
        )}
      </div>
    </div>
  );
}
